import Phaser from 'phaser';
import { SceneKeys } from './SceneKeys';
import { gameEvents } from '../core/events';
import { Rng } from '../core/rng';
import { createRunState } from '../core/state';
import { TEX } from '../core/TextureFactory';
import { biomePaletteFor, type BiomePalette } from '../core/biomePalette';
import type { BoardDef, ChapterDef, RunState, WaveDef } from '../core/types';
import { boards, cards, chapters } from '../data';
import { CombatView } from '../combat';
import { PlinkoView } from '../plinko/PlinkoView';
import { ShopView } from '../ui/shop/ShopView';
import { AudioToggleButton } from '../ui/AudioToggleButton';
import type { ResultsSceneData } from './ResultsScene';

/**
 * Datos que la escena Lobby pasa al arrancar una run
 * (`this.scene.start(SceneKeys.Game, data)`).
 */
export interface GameSceneData {
  runState?: RunState;
  chapterId?: string;
  chapterIndex?: number;
}

/** Fases del loop de una oleada dentro de la run. */
type Phase = 'fighting' | 'pouring' | 'shopping' | 'ending';

const HUD_TEXT = '#f4f0ff';
const HUD_MUTED = '#a99fd1';
const COMBAT_FRAC = 0.3;

/**
 * GameScene — máquina de estados del loop completo de una run:
 * `FIGHTING → (COLLECTING interno del combate) → POURING → SHOPPING → FIGHTING
 * … → BOSS → RESULTS`.
 *
 * Conecta los cuatro sistemas construidos en paralelo vía el bus de eventos
 * tipado (`core/events.ts`): `CombatView` en la franja superior (~30%),
 * `PlinkoView`/`ShopView` en la zona inferior (~70%), y el HUD superpuesto
 * (bolas, WAVE n/m con barra rematada en trofeo, velocidad ×1/×2 y pausa).
 * Al terminar la run navega a `ResultsScene` con las oleadas superadas para
 * que la meta calcule las recompensas.
 */
export class GameScene extends Phaser.Scene {
  private run!: RunState;
  private chapter!: ChapterDef;
  private chapterIndex = 0;
  private rng!: Rng;

  private combat!: CombatView;
  private plinko!: PlinkoView;
  private shop?: ShopView;

  private phase: Phase = 'fighting';
  /** Oleadas completamente superadas en esta run (para las recompensas parciales). */
  private wavesCleared = 0;
  /** Bolas cosechadas en la oleada actual, que van al vaso para verter (no al banco todavía). */
  private pendingBalls = 0;
  private paused = false;

  /** Valor de bolas de banco actualmente mostrado en el HUD (persigue a `run.balls` con un tween). */
  private displayedBalls = 0;
  private ballsTween?: Phaser.Tweens.Tween;

  // -- HUD --------------------------------------------------------------
  private hud!: Phaser.GameObjects.Container;
  private ballsText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private progressFill!: Phaser.GameObjects.Rectangle;
  private progressWidth = 0;
  private trophy!: Phaser.GameObjects.Image;
  private speedText!: Phaser.GameObjects.Text;
  private pauseText!: Phaser.GameObjects.Text;
  private bossLabel!: Phaser.GameObjects.Text;

  private travelText!: Phaser.GameObjects.Text;
  private pauseOverlay!: Phaser.GameObjects.Container;

  private readonly unsubs: Array<() => void> = [];

  constructor() {
    super(SceneKeys.Game);
  }

  init(data: GameSceneData): void {
    this.chapterIndex = data.chapterIndex ?? 0;
    this.chapter = chapters[this.chapterIndex] ?? (chapters[0] as ChapterDef);
    this.run =
      data.runState ??
      createRunState({ chapterId: this.chapter.id, totalWaves: this.chapter.waves.length });
    // Asegura coherencia por si vino un runState de otro capítulo (o defaults).
    this.run.chapterId = this.chapter.id;
    this.run.totalWaves = this.chapter.waves.length;
    this.wavesCleared = 0;
    this.pendingBalls = 0;
    this.phase = 'fighting';
    this.paused = false;
    this.displayedBalls = this.run.balls;
    // Semilla determinista por capítulo+tiempo: reproducible dentro de la run.
    this.rng = new Rng((Date.now() ^ (this.chapterIndex + 1) * 0x9e3779b9) >>> 0);
  }

  create(): void {
    const { width, height } = this.scale;
    const combatHeight = Math.round(height * COMBAT_FRAC);

    this.cameras.main.setBackgroundColor('#140c26');
    this.drawZones(width, height, combatHeight);

    // -- Combate (franja superior ~30%) ---------------------------------
    this.combat = new CombatView();
    this.combat.mount(this, { x: 0, y: 0, width, height: combatHeight });

    // -- Plinko (zona inferior ~70%) ------------------------------------
    this.plinko = new PlinkoView();
    this.plinko.mount(this, {
      x: 0,
      y: combatHeight,
      width,
      height: height - combatHeight,
    });

    // Texto "Viaje en curso..." mientras se pelea (tablero oculto).
    this.travelText = this.add
      .text(width / 2, combatHeight + (height - combatHeight) / 2, 'Viaje en curso...', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: HUD_MUTED,
      })
      .setOrigin(0.5)
      .setDepth(40);

    this.buildHud(width, combatHeight);
    this.buildPauseOverlay(width, height);

    this.subscribeEvents();

    this.events.once('shutdown', this.cleanup, this);
    this.events.once('destroy', this.cleanup, this);

    this.exposeDebugBridge();

    this.startWaveFlow();
  }

  // -- Layout -----------------------------------------------------------

  /**
   * Fondo + props de la franja de combate y fondo/tinte del tablero,
   * según la paleta del bioma del capítulo actual (`chapter.biome`, ver
   * `core/biomePalette.ts`). Mismo layout en los 4 biomas — solo cambian
   * colores y las siluetas decorativas.
   */
  private drawZones(width: number, height: number, combatHeight: number): void {
    const palette = biomePaletteFor(this.chapter.biome);

    const sky = this.add.graphics().setDepth(-5);
    sky.fillGradientStyle(palette.skyTop, palette.skyTop, palette.skyBottom, palette.skyBottom, 1);
    sky.fillRect(0, 0, width, combatHeight);
    // suelo del combate
    sky.fillStyle(palette.groundTop, 1);
    sky.fillRect(0, combatHeight * 0.78, width, combatHeight * 0.22);
    sky.fillStyle(palette.groundBottom, 1);
    sky.fillRect(0, combatHeight * 0.95, width, combatHeight * 0.05);

    this.drawBiomeProps(width, combatHeight, palette);

    // tinte del tablero: mismo degradado violeta oscuro de siempre, pero
    // corrido hacia el acento del bioma para que se sienta parte del mismo
    // capítulo (sin perder contraste con barras/texto, que no se tocan).
    const board = this.add.graphics().setDepth(-5);
    board.fillGradientStyle(palette.boardTop, palette.boardTop, palette.boardBottom, palette.boardBottom, 1);
    board.fillRect(0, combatHeight, width, height - combatHeight);
  }

  /** Siluetas decorativas de fondo (por encima del cielo, detrás del combate) según el bioma. */
  private drawBiomeProps(width: number, combatHeight: number, palette: BiomePalette): void {
    const g = this.add.graphics().setDepth(-4);
    const horizonY = combatHeight * 0.56;
    const positions = [width * 0.14, width * 0.44, width * 0.8];

    switch (this.chapter.biome) {
      case 'desierto':
        positions.forEach((x) => this.drawCactusProp(g, x, horizonY, combatHeight, palette));
        break;
      case 'cripta':
        positions.forEach((x) => this.drawGravestoneProp(g, x, horizonY, combatHeight, palette));
        this.drawFogProp(g, width, horizonY + combatHeight * 0.14, palette);
        break;
      case 'volcan':
        positions.forEach((x) => this.drawLavaRockProp(g, x, horizonY, combatHeight, palette));
        break;
      case 'bosque':
      default:
        positions.forEach((x) => this.drawTreeProp(g, x, horizonY, combatHeight, palette));
        break;
    }
  }

  private drawTreeProp(
    g: Phaser.GameObjects.Graphics,
    x: number,
    baseY: number,
    combatHeight: number,
    palette: BiomePalette
  ): void {
    const h = combatHeight * 0.22;
    g.fillStyle(palette.propPrimary, 0.8);
    g.fillRect(x - h * 0.06, baseY - h * 0.42, h * 0.12, h * 0.42);
    g.fillStyle(palette.propSecondary, 0.7);
    g.fillCircle(x, baseY - h * 0.55, h * 0.34);
    g.fillCircle(x - h * 0.22, baseY - h * 0.42, h * 0.24);
    g.fillCircle(x + h * 0.22, baseY - h * 0.44, h * 0.26);
  }

  private drawCactusProp(
    g: Phaser.GameObjects.Graphics,
    x: number,
    baseY: number,
    combatHeight: number,
    palette: BiomePalette
  ): void {
    const h = combatHeight * 0.24;
    g.fillStyle(palette.propPrimary, 0.75);
    g.fillRoundedRect(x - h * 0.08, baseY - h * 0.6, h * 0.16, h * 0.6, h * 0.06);
    g.fillRoundedRect(x - h * 0.32, baseY - h * 0.38, h * 0.16, h * 0.3, h * 0.06);
    g.fillRoundedRect(x + h * 0.16, baseY - h * 0.48, h * 0.16, h * 0.32, h * 0.06);
    g.fillStyle(palette.propSecondary, 0.35);
    g.fillEllipse(x, baseY + 2, h * 0.9, h * 0.14);
  }

  private drawGravestoneProp(
    g: Phaser.GameObjects.Graphics,
    x: number,
    baseY: number,
    combatHeight: number,
    palette: BiomePalette
  ): void {
    const h = combatHeight * 0.2;
    g.fillStyle(palette.propPrimary, 0.75);
    g.fillRoundedRect(x - h * 0.22, baseY - h * 0.5, h * 0.44, h * 0.5, h * 0.2);
    g.fillStyle(palette.propSecondary, 0.6);
    g.fillRect(x - h * 0.03, baseY - h * 0.4, h * 0.06, h * 0.18);
    g.fillRect(x - h * 0.11, baseY - h * 0.32, h * 0.22, h * 0.06);
  }

  private drawFogProp(g: Phaser.GameObjects.Graphics, width: number, y: number, palette: BiomePalette): void {
    g.fillStyle(palette.propSecondary, 0.16);
    g.fillEllipse(width * 0.5, y, width * 1.1, 26);
  }

  private drawLavaRockProp(
    g: Phaser.GameObjects.Graphics,
    x: number,
    baseY: number,
    combatHeight: number,
    palette: BiomePalette
  ): void {
    const h = combatHeight * 0.22;
    g.fillStyle(palette.propPrimary, 0.8);
    g.fillTriangle(x - h * 0.32, baseY, x, baseY - h * 0.6, x + h * 0.1, baseY);
    g.fillTriangle(x - h * 0.05, baseY, x + h * 0.22, baseY - h * 0.42, x + h * 0.4, baseY);
    g.fillStyle(palette.propSecondary, 0.85);
    g.fillCircle(x - h * 0.05, baseY - h * 0.22, h * 0.05);
    g.fillCircle(x + h * 0.2, baseY - h * 0.16, h * 0.04);
  }

  // -- HUD --------------------------------------------------------------

  private buildHud(width: number, combatHeight: number): void {
    this.hud = this.add.container(0, 0).setDepth(100);

    // fondo translúcido de la barra superior
    const bar = this.add.graphics();
    bar.fillStyle(0x0f0820, 0.55);
    bar.fillRect(0, 0, width, 40);
    this.hud.add(bar);

    // contador de bolas (arriba-izq)
    const ballIcon = this.add.image(16, 20, TEX.ball).setDisplaySize(16, 16).setOrigin(0.5);
    this.ballsText = this.add
      .text(30, 20, '0', {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: HUD_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    this.hud.add([ballIcon, this.ballsText]);

    // WAVE n/m + barra de progreso rematada en trofeo (centro)
    const centerX = width / 2;
    this.waveText = this.add
      .text(centerX, 11, 'WAVE 1/1', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: HUD_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);
    this.hud.add(this.waveText);

    this.progressWidth = Math.min(160, width * 0.36);
    const barX = centerX - this.progressWidth / 2;
    const barY = 30;
    const trackBg = this.add
      .rectangle(barX, barY, this.progressWidth, 6, 0x1b1030)
      .setOrigin(0, 0.5);
    trackBg.setStrokeStyle(1, 0x453a70);
    this.progressFill = this.add
      .rectangle(barX + 1, barY, 0, 4, 0xffd166)
      .setOrigin(0, 0.5);
    this.trophy = this.add
      .image(barX + this.progressWidth + 8, barY, TEX.iconCoin)
      .setDisplaySize(14, 14)
      .setOrigin(0.5);
    this.hud.add([trackBg, this.progressFill, this.trophy]);

    // velocidad ×1/×2 y pausa (arriba-der). El área táctil se fija a ≥44px
    // ANTES de `setInteractive` (que por defecto usa el tamaño del objeto en
    // ese momento) para que el toque sea preciso sin agrandar el glifo.
    const TOUCH_TARGET = 44;
    this.speedText = this.add
      .text(width - 78, 20, '×1', {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#8ecbff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlign('center');
    this.speedText.setFixedSize(TOUCH_TARGET, TOUCH_TARGET);
    this.speedText.setInteractive({ useHandCursor: true }).on('pointerdown', () => this.toggleSpeed());

    this.pauseText = this.add
      .text(width - 24, 20, '❚❚', {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: HUD_TEXT,
      })
      .setOrigin(0.5)
      .setAlign('center');
    this.pauseText.setFixedSize(TOUCH_TARGET, TOUCH_TARGET);
    this.pauseText.setInteractive({ useHandCursor: true }).on('pointerdown', () => this.togglePause());
    this.hud.add([this.speedText, this.pauseText]);

    // silenciar/activar sonido (persiste entre escenas vía AudioManager).
    const muteButton = new AudioToggleButton(this, width - 132, 20);
    this.hud.add(muteButton);

    // etiqueta de jefe (oculta hasta combat:bossSpawned)
    this.bossLabel = this.add
      .text(centerX, combatHeight - 12, '', {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#ff9f9f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(100)
      .setVisible(false);

    this.refreshHud();
  }

  private refreshHud(): void {
    // banco + (bolas por verter de esta oleada), p.ej. "180 (+7)"; el banco
    // "sube" animado en vez de saltar directo al nuevo valor.
    if (this.displayedBalls !== this.run.balls) {
      this.animateBallsTo(this.run.balls);
    } else {
      this.renderBallsText();
    }
    this.waveText.setText(`WAVE ${Math.min(this.run.wave, this.run.totalWaves)}/${this.run.totalWaves}`);
    const ratio = Phaser.Math.Clamp(this.run.wave / this.run.totalWaves, 0, 1);
    this.progressFill.width = Math.max(0, (this.progressWidth - 2) * ratio);
    this.speedText.setText(`×${this.run.speed}`);
  }

  /** Anima el contador de bolas del banco desde su valor mostrado actual hasta `target`. */
  private animateBallsTo(target: number): void {
    this.ballsTween?.remove();
    const proxy = { value: this.displayedBalls };
    this.ballsTween = this.tweens.add({
      targets: proxy,
      value: target,
      duration: 480,
      ease: 'Cubic.Out',
      onUpdate: () => {
        this.displayedBalls = Math.round(proxy.value);
        this.renderBallsText();
      },
      onComplete: () => {
        this.displayedBalls = target;
        this.renderBallsText();
      },
    });
  }

  private renderBallsText(): void {
    this.ballsText.setText(
      this.pendingBalls > 0 ? `${this.displayedBalls} (+${this.pendingBalls})` : `${this.displayedBalls}`
    );
  }

  private buildPauseOverlay(width: number, height: number): void {
    this.pauseOverlay = this.add.container(0, 0).setDepth(200).setVisible(false);
    const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.55).setOrigin(0, 0);
    dim.setInteractive();
    dim.on('pointerdown', () => this.togglePause());
    const label = this.add
      .text(width / 2, height / 2, 'PAUSA\n\ntoca para continuar', {
        fontFamily: 'monospace',
        fontSize: '20px',
        color: HUD_TEXT,
        align: 'center',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.pauseOverlay.add([dim, label]);
  }

  private toggleSpeed(): void {
    this.run.speed = this.run.speed === 1 ? 2 : 1;
    gameEvents.emit('ui:speedChanged', { speed: this.run.speed });
    this.refreshHud();
  }

  private togglePause(): void {
    this.paused = !this.paused;
    this.pauseOverlay.setVisible(this.paused);
    if (this.paused) {
      this.matter.world.pause();
    } else {
      this.matter.world.resume();
    }
  }

  // -- Eventos ----------------------------------------------------------

  private subscribeEvents(): void {
    this.unsubs.push(
      gameEvents.on('combat:enemyKilled', ({ balls }) => {
        // Las bolas soltadas se cosechan en el "vaso" de la oleada; recién
        // tras verterlas (multiplicadas) entran al banco de la run.
        this.pendingBalls += balls;
        this.refreshHud();
      })
    );
    this.unsubs.push(
      gameEvents.on('combat:heroDied', () => this.handleHeroDeath())
    );
    this.unsubs.push(
      gameEvents.on('combat:waveCleared', () => this.handleWaveCleared())
    );
    this.unsubs.push(
      gameEvents.on('combat:bossSpawned', ({ name }) => {
        this.bossLabel.setText(name).setVisible(true);
      })
    );
  }

  // -- Máquina de estados ----------------------------------------------

  private startWaveFlow(): void {
    this.phase = 'fighting';
    this.travelText.setVisible(true);
    this.bossLabel.setVisible(false);

    const def = this.chapter.waves[this.run.wave - 1] as WaveDef | undefined;
    if (!def) {
      // sin oleada definida: se considera capítulo superado
      this.endRun('won');
      return;
    }

    // Vida máxima efectiva = base de la run * (1 + maxHpPct de cartas).
    const effMaxHp = this.effectiveMaxHp();
    const hp = Math.min(this.run.hp, effMaxHp);

    this.combat.setStats(this.run.stats);
    this.combat.startWave(def, {
      stats: this.run.stats,
      hp,
      maxHp: effMaxHp,
      wave: this.run.wave,
      total: this.run.totalWaves,
    });
    this.refreshHud();
  }

  private effectiveMaxHp(): number {
    return Math.max(1, Math.round(this.run.maxHp * (1 + this.run.stats.maxHpPct / 100)));
  }

  private handleWaveCleared(): void {
    if (this.phase !== 'fighting') return;
    // persiste la vida real del héroe (incluye curación por robo de vida)
    this.run.hp = Math.max(1, Math.round(this.combat.getHeroHp()));
    // el buff "solo próxima oleada" se consume al terminar esta oleada
    this.run.stats = { ...this.run.stats, nextWaveAtkPct: 0 };
    this.wavesCleared += 1;

    const def = this.chapter.waves[this.run.wave - 1];
    if (def?.isBoss || this.run.wave >= this.run.totalWaves) {
      this.endRun('won');
      return;
    }
    this.startPour();
  }

  private handleHeroDeath(): void {
    if (this.phase !== 'fighting') return;
    if (!this.run.reviveUsed) {
      this.run.reviveUsed = true;
      const reviveHp = Math.max(1, Math.round(this.effectiveMaxHp() * 0.3));
      this.run.hp = reviveHp;
      this.combat.reviveHero(reviveHp);
      gameEvents.emit('run:revived', {});
      this.showRevivePopup();
      return;
    }
    this.endRun('lost');
  }

  private showRevivePopup(): void {
    const { width, height } = this.scale;
    const combatHeight = Math.round(height * COMBAT_FRAC);
    const popup = this.add
      .text(width / 2, combatHeight * 0.5, '¡Segunda oportunidad!', {
        fontFamily: 'monospace',
        fontSize: '22px',
        color: '#ffd166',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(150)
      .setScale(0.6)
      .setAlpha(0);
    this.tweens.add({
      targets: popup,
      scale: 1,
      alpha: 1,
      duration: 260,
      ease: 'Back.Out',
      yoyo: true,
      hold: 900,
      onComplete: () => popup.destroy(),
    });
  }

  private startPour(): void {
    this.phase = 'pouring';
    this.travelText.setVisible(false);

    const boardId = this.chapter.boardTable[this.run.wave - 1];
    const boardDef =
      boards.find((b) => b.id === boardId) ?? (boards[0] as BoardDef);

    this.plinko
      .start(this.pendingBalls, boardDef, this.run.boardMods, this.chapter.biome)
      .then(({ ballsGained }) => this.onPourDone(ballsGained))
      .catch(() => this.onPourDone(this.pendingBalls));
  }

  private onPourDone(ballsGained: number): void {
    if (this.phase !== 'pouring') return;
    // el resultado multiplicado del vertido entra al banco de la run
    this.run.balls += ballsGained;
    this.pendingBalls = 0;
    this.refreshHud();
    this.startShop();
  }

  private startShop(): void {
    this.phase = 'shopping';
    const { width, height } = this.scale;
    const combatHeight = Math.round(height * COMBAT_FRAC);
    const boardHeight = height - combatHeight;

    const offClosed = gameEvents.once('shop:closed', () => {
      offClosed();
      this.shop = undefined;
      this.advanceToNextWave();
    });

    this.shop = new ShopView({
      scene: this,
      x: width / 2,
      y: combatHeight + boardHeight / 2,
      width: Math.min(width - 24, 420),
      height: Math.min(boardHeight - 24, 460),
      run: this.run,
      cardsPool: cards,
      wave: this.run.wave,
      chapterNumber: this.chapterIndex + 1,
      rng: this.rng,
      onRunStateChange: (next) => {
        this.run = next;
        this.combat.setStats(this.run.stats);
        this.refreshHud();
      },
    });
  }

  private advanceToNextWave(): void {
    this.run.wave += 1;
    this.startWaveFlow();
  }

  private endRun(outcome: 'won' | 'lost'): void {
    if (this.phase === 'ending') return;
    this.phase = 'ending';
    if (outcome === 'won') {
      gameEvents.emit('run:won', { rewards: this.chapter.rewards });
    } else {
      gameEvents.emit('run:lost', {
        partialRewards: { coins: this.chapter.rewards.coins, xp: this.chapter.rewards.xp },
      });
    }
    const data: ResultsSceneData = {
      chapterIndex: this.chapterIndex,
      outcome,
      wavesCleared: this.wavesCleared,
    };
    // pequeño respiro antes de cambiar de escena
    this.time.delayedCall(500, () => this.scene.start(SceneKeys.Results, data));
  }

  // -- Loop -------------------------------------------------------------

  update(time: number, delta: number): void {
    if (this.paused) return;
    this.combat?.update(time, delta);
  }

  private cleanup(): void {
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    this.combat?.destroy();
    this.plinko?.destroy();
    this.shop?.destroy();
    this.shop = undefined;
    delete (window as unknown as { __vyv?: unknown }).__vyv;
  }

  // -- Puente de depuración/e2e ----------------------------------------

  /**
   * Expone un puente mínimo en `window.__vyv` para que las pruebas e2e
   * (Playwright) puedan observar el estado y disparar el vertido con un
   * arrastre sintético, sin depender del timing físico del arrastre real.
   */
  private exposeDebugBridge(): void {
    (window as unknown as { __vyv?: unknown }).__vyv = {
      phase: () => this.phase,
      balls: () => this.run.balls,
      pending: () => this.pendingBalls,
      wave: () => this.run.wave,
      hp: () => Math.round(this.combat.getHeroHp()),
      wavesCleared: () => this.wavesCleared,
      shopOpen: () => this.shop !== undefined,
      speed: () => this.run.speed,
      x2: () => {
        if (this.run.speed === 1) this.toggleSpeed();
      },
      pour: () => this.plinko.debugPour(),
      buyFirst: () => this.shop?.debugBuyFirst() ?? false,
      forceWin: () => this.endRun('won'),
      forceLose: () => this.endRun('lost'),
    };
  }
}
